import { catchError, first, map, tap } from 'rxjs/operators';
import rfdc from 'rfdc';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { UnitMeasurement } from '../../../assets/data/enum/unit_measurement.enum';
import { environment } from '../../../environments/environment';
import { User } from '../_models';
import { StableActivity } from '../_models/stableActivity';
import { UserService } from './user.service';
import { StableActivityGrouped } from '../_models/stableActivityGrouped';

@Injectable()
export class StableActivityService {
  // Services
  private http = inject(HttpClient);
  private userService = inject(UserService);
  private clone = rfdc();

  ItemObj: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private apiBaseUrl = environment.apiBaseUrl;
  private unitMeasurement = UnitMeasurement;
  currentUser: User = new User();
  allStableActivity$: BehaviorSubject<Array<StableActivity>> = new BehaviorSubject<
    Array<StableActivity>
  >([]);
  allStableActivityGrouped$: BehaviorSubject<Array<StableActivityGrouped>> = new BehaviorSubject<
    Array<StableActivityGrouped>
  >([]);

  constructor() {
    this.userService.getUserMeFromLocalStore().subscribe((u: User) => {
      this.currentUser = u;
    });
  }

  getStableActivities(): Observable<StableActivity[]> {
    return this.http.get<any>(this.apiBaseUrl + 'api/private/v1/stableActivities').pipe(
      map((response) => {
        if (!response) {
          return [];
        }

        this.allStableActivity$.next(response);
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching stable activities:', error);
        return of([]);
      })
    );
  }

  deleteStableActivity(stableActivity: StableActivity): Observable<StableActivity> {
    return this.http
      .delete<void>(this.apiBaseUrl + 'api/private/v1/stableActivities/' + stableActivity.id)
      .pipe(map(() => ({ id: stableActivity.id }) as StableActivity));
  }

  getLocalStablectivityById = (id: number): StableActivity => {
    let item: StableActivity;
    this.allStableActivity$
      .pipe(first((list: StableActivity[]) => list && list.length > 0))
      .subscribe((list: StableActivity[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  getLocalStableActivityByUUID(uuid: string): StableActivity[] {
    if (!uuid) {
      return [];
    }

    return this.allStableActivity$.getValue().filter((f) => f.uuid === uuid);
  }

  getUnitMeasurementConfigurationLivestock() {
    return [
      this.unitMeasurement.kg_animal,
      // this.unitMeasurement.g_animal, // more info in XFARMDEV-4704
      // this.unitMeasurement.mg_animal, // more info in XFARMDEV-4704
      this.unitMeasurement.l_animal,
      this.unitMeasurement.unit_animal,
    ];
  }

  getStableActivityGrouped(): Observable<StableActivityGrouped[]> {
    return this.http
      .get<StableActivityGrouped[]>(this.apiBaseUrl + 'api/private/v1/stableActivityGrouped')
      .pipe(tap((response) => this.allStableActivityGrouped$.next(response)));
  }

  checkIfStableBoxExistInStableActivity(idStableBox: number): Observable<StableActivity[]> {
    if (!idStableBox) {
      return of([]);
    }

    return this.allStableActivity$.pipe(
      map((stableActivityList: StableActivity[]) =>
        stableActivityList.filter((f) => f.stableBox && f.stableBox.id === idStableBox)
      )
    );
  }

  getActivityForClone(stableActivity: StableActivity): StableActivity {
    const sAToBeCloned: StableActivity = this.clone(stableActivity);
    delete sAToBeCloned.id;
    delete sAToBeCloned.uuid;
    if (sAToBeCloned.mixes && sAToBeCloned.mixes.length > 0) {
      sAToBeCloned.mixes = sAToBeCloned.mixes.map((mix) => {
        delete mix.stableActivity;
        delete mix.uuid;
        return mix;
      });
    }
    return sAToBeCloned;
  }

  getStableActivityGroupedForClone(SAGrouped: StableActivityGrouped) {
    delete SAGrouped.uuid;
    delete SAGrouped.id;
    return SAGrouped;
  }

  getExportType() {
    const result: Array<any> = [
      {
        type: 'StableActivities',
        subtype: 'CompleteStableActivityListToLocalExport',
        formats: [
          {
            type: 'csv',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
    ];
    return result;
  }

  checkIfAnimalIdExistInStableActivity(animalId: number): Array<any> {
    const tmpActivity = [];
    this.allStableActivity$.subscribe((activities) => {
      activities.forEach((activity) => {
        if (activity && activity.animals && activity.animals.length > 0) {
          activity.animals.forEach((a) => {
            if (a && a.id && a.id === animalId) {
              tmpActivity.push(activity);
            }
          });
        }
      });
    });

    return tmpActivity;
  }

  getLocalStableActivityDone(): StableActivityGrouped[] {
    return this.allStableActivityGrouped$
      .getValue()
      .filter((f) => f.done === true || f.done === null || f.done === undefined);
  }

  retrieveLastDoneStableActivityFromStable(stable): StableActivity {
    const filteredLocalStableActivityDone = this.getLocalStableActivityDone().filter((item) =>
      item.stableIds.includes(stable.id)
    );
    if (filteredLocalStableActivityDone && filteredLocalStableActivityDone.length > 0) {
      // If there is at least one activity with the searched ID, return the last one based on the creation date.
      const lastFieldActivity = filteredLocalStableActivityDone.reduce((prev, current) =>
        new Date(prev.activityDate + '') > new Date(current.activityDate + '') ? prev : current
      );
      return this.getLocalStablectivityById(lastFieldActivity.ids[0]);
    }

    return null;
  }
}
